import { authFetch, Fetch } from "@arachnys/flow-app/src/api";
import { downloadFile } from "@arachnys/flow-app/src/util";
import { css } from "glamor";
import React from "react";
import qs from "qs";

const styles = {
    docUL: css({
        listStyle: "none",
        paddingLeft: 10,
    }),
    docLI: css({
        whiteSpace: "normal",
        wordBreak: "break-word",
    }),
    downloadLink: css({
        ":hover": {
            textDecoration: "underline",
        },
    }),
};

export type DocumentDatum = {
    documentId: string;
    documentName: string;
    documentTitle?: string;
};

export const fetchDocumentData = async (
    queryParams: object,
    documentFilter?: Function,
    docstoreBaseUrl: string = "/docstore"
): Promise<DocumentDatum[]> => {
    const query = qs.stringify(queryParams, { encode: false });
    const request = new Request(`${docstoreBaseUrl}/doc/search?${query}`, {
        headers: { Accept: "application/pdf, image/*" },
    });
    const response = await Fetch(request);
    const documents = documentFilter
        ? response.filter(documentFilter)
        : response;
    return documents.map((document: any) => ({
        documentId: document.id,
        documentName: document.title || document.filename,
    }));
};

export const DocumentLink = ({
    documentDatum,
    docstoreBaseUrl = "/docstore",
}: {
    documentDatum: DocumentDatum;
    docstoreBaseUrl?: string;
}) => {
    const onDocumentClick = async (
        event: React.MouseEvent<HTMLAnchorElement>
    ) => {
        event.preventDefault();
        const { download: name, href } = event.currentTarget;
        const request = new Request(href, {
            headers: { Accept: "application/pdf, image/*" },
        });
        const response = await authFetch(request);
        downloadFile(name)(response);
    };

    const { documentId, documentName, documentTitle } = documentDatum;

    return (
        <a
            download={documentName}
            href={`${docstoreBaseUrl}/doc/${documentId}/download`}
            onClick={onDocumentClick}
            {...styles.downloadLink}
        >
            {documentTitle || documentName}
        </a>
    );
};

export const DocumentList = ({
    documentData,
    docstoreBaseUrl,
}: {
    documentData?: DocumentDatum[];
    docstoreBaseUrl?: string;
}) => (
    <ul {...styles.docUL}>
        {documentData &&
            documentData.map(documentDatum => (
                <li {...styles.docLI} key={documentDatum.documentId}>
                    <DocumentLink
                        documentDatum={documentDatum}
                        docstoreBaseUrl={docstoreBaseUrl}
                    />
                </li>
            ))}
    </ul>
);

type DocumentListWithFetchProps = {
    queryParams: object;
    docstoreBaseUrl?: string;
    documentFilter?: Function;
};

type DocumentListWithFetchState = {
    documentData: DocumentDatum[];
};

export class DocumentListWithFetch extends React.Component<
    DocumentListWithFetchProps,
    DocumentListWithFetchState
> {
    state = {
        documentData: [],
    };

    async componentDidMount() {
        this.updateStateFromProps();
    }

    async componentDidUpdate(prevProps: DocumentListWithFetchProps) {
        if (prevProps !== this.props) {
            this.updateStateFromProps();
        }
    }

    async updateStateFromProps() {
        const { queryParams, documentFilter, docstoreBaseUrl } = this.props;
        this.setState({
            documentData: await fetchDocumentData(
                queryParams,
                documentFilter,
                docstoreBaseUrl
            ),
        });
    }

    render() {
        return (
            <DocumentList
                documentData={this.state.documentData}
                docstoreBaseUrl={this.props.docstoreBaseUrl}
            />
        );
    }
}
